import React, { useState, useEffect } from "react";
import { TrendingUp, Clock, CheckCircle, AlertTriangle, ChevronDown, Calendar, RefreshCw } from "lucide-react";

import { getApiHistory } from "../hooks/fetching/getApiHistory";

interface TimeFilter {
  label: string;
  value: string;
  hours: number;
}

const APIUsageHistoryPage: React.FC = () => {
  const [selectedFilter, setSelectedFilter] = useState<TimeFilter>({
    label: "Last 7 days",
    value: "7d",
    hours: 168,
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [stats, setStats] = useState({
    totalRequests: 0,
    avgResponse: 0,
    successRate: 0,
    errorsToday: 0,
  });

  const [recentRequests, setRecentRequests] = useState<any[]>([]);

  // pagination state
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;
  const [hasMore, setHasMore] = useState(true);

  const timeFilters: TimeFilter[] = [
    { label: "Last 24 hours", value: "24h", hours: 24 },
    { label: "Last 7 days", value: "7d", hours: 168 },
    { label: "Last 30 days", value: "30d", hours: 720 },
    { label: "Last 90 days", value: "90d", hours: 2160 },
  ];

  // ============================
  // Load first page
  // ============================
  const loadData = async () => {
    try {
      setIsLoading(true);

      const created_after = new Date(Date.now() - selectedFilter.hours * 60 * 60 * 1000).toISOString();

      const res = await getApiHistory({
        page: 1,
        items_per_page: itemsPerPage,
        created_after,
        sort_by: "created_at",
        sort_direction: "desc",
      });

      setRecentRequests(res.data);
      setPage(1);

      // update stats
      setStats({
        totalRequests: res.stats.total_requests,
        avgResponse: res.stats.avg_response_ms,
        successRate: res.stats.success_rate,
        errorsToday: res.stats.errors_today,
      });

      setHasMore(res.page < res.total_pages);
    } catch (err) {
      console.error("Failed to load API usage:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================
  // Load MORE pages
  // ============================
  const loadMore = async () => {
    try {
      const nextPage = page + 1;

      const created_after = new Date(Date.now() - selectedFilter.hours * 60 * 60 * 1000).toISOString();

      const res = await getApiHistory({
        page: nextPage,
        items_per_page: itemsPerPage,
        created_after,
        sort_by: "created_at",
        sort_direction: "desc",
      });

      setRecentRequests((prev) => [...prev, ...res.data]);
      setPage(nextPage);

      setHasMore(nextPage < res.total_pages);
    } catch (err) {
      console.error("Error loading more:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedFilter]);

  const handleFilterChange = async (filter: TimeFilter): Promise<void> => {
    setSelectedFilter(filter);
    setIsFilterOpen(false);
  };

  const handleRefresh = async (): Promise<void> => {
    await loadData();
  };

  const handleViewAll = (): void => {
    console.log("Navigate to full analytics");
  };

  const getMethodBadgeColors = (method: string): string => {
    switch (method) {
      case "GET":
        return "bg-green-50 text-green-700";
      case "POST":
        return "bg-blue-50 text-blue-700";
      case "PUT":
        return "bg-yellow-50 text-yellow-700";
      case "DELETE":
        return "bg-red-50 text-red-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  const getStatusBadgeColors = (status: number): string => {
    if (status >= 200 && status < 300) return "bg-green-100 text-green-800";
    if (status >= 300 && status < 400) return "bg-yellow-100 text-yellow-800";
    if (status >= 400) return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  const getResponseTimeColor = (responseTime: number): string => {
    if (responseTime < 200) return "text-gray-900";
    if (responseTime < 500) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className="min-h-screen animate-in fade-in slide-in-from-bottom-8 duration-400 ease-out">
      <div className=" mx-auto p-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <h1 className="text-gray-900 font-semibold text-2xl font-inter">API Usage History</h1>
              {isLoading && <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />}
            </div>
            <p className="text-gray-500 font-normal text-sm">Monitor recent API requests and performance</p>
          </div>

          <div className="flex items-center gap-3">
            {/* Time Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                disabled={isLoading}
                className="bg-white border border-gray-200 rounded-lg px-4 py-2.5 flex items-center gap-2 hover:bg-gray-50 disabled:opacity-50"
              >
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-gray-900 font-medium text-sm">{selectedFilter.label}</span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {isFilterOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px] z-10">
                  {timeFilters.map((filter) => (
                    <button
                      key={filter.value}
                      onClick={() => handleFilterChange(filter)}
                      className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 ${
                        selectedFilter.value === filter.value ? "bg-orange-50 text-xuron-orange-dark" : "text-gray-700"
                      }`}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full px-6 py-2.5 disabled:opacity-50"
            >
              <span className="text-gray-500 font-medium text-sm">Refresh</span>
            </button>

            {/* <button
              onClick={handleViewAll}
              className="bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-full px-6 py-2.5"
            >
              <span className="text-gray-500 font-medium text-sm">View All</span>
            </button> */}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Requests */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 font-medium text-xs">Total Requests</span>
              <div className="w-4 h-4 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-2.5 h-2.5 text-green-600" />
              </div>
            </div>
            <span className="text-gray-900 font-bold text-xl">{stats.totalRequests.toLocaleString()}</span>
          </div>

          {/* Avg Response */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 font-medium text-xs">Avg Response</span>
              <div className="w-4 h-4 bg-blue-100 rounded-lg flex items-center justify-center">
                <Clock className="w-2.5 h-2.5 text-blue-600" />
              </div>
            </div>
            <span className="text-gray-900 font-bold text-xl">{stats.avgResponse}ms</span>
          </div>

          {/* Success Rate */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 font-medium text-xs">Success Rate</span>
              <div className="w-4 h-4 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-2.5 h-2.5 text-green-600" />
              </div>
            </div>
            <span className="text-gray-900 font-bold text-xl">{stats.successRate}%</span>
          </div>

          {/* Errors Today */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-gray-500 font-medium text-xs">Errors Today</span>
              <div className="w-4 h-4 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-2.5 h-2.5 text-red-600" />
              </div>
            </div>
            <span className="text-red-600 font-bold text-xl">{stats.errorsToday}</span>
          </div>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <div className="grid grid-cols-12 gap-4 items-center">
              <div className="col-span-2">
                <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">Time</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">API Key</span>
              </div>
              <div className="col-span-4">
                <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">Endpoint</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">Response</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500 font-semibold text-xs uppercase tracking-wide">Status</span>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-200">
            {recentRequests.map((r) => (
              <div key={r.id} className="px-5 py-4 hover:bg-gray-50 transition-colors">
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-2">
                    <span className="text-gray-900 font-medium text-sm">{r.time}</span>
                  </div>

                  <div className="col-span-2">
                    <span className="text-gray-500 font-normal text-xs">{r.apiKey}</span>
                  </div>

                  <div className="col-span-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-lg font-semibold text-xs ${getMethodBadgeColors(r.method)}`}>
                        {r.method}
                      </span>
                      <code className="text-gray-900 font-mono text-sm text-nowrap overflow-hidden overflow-ellipsis">
                        {r.endpoint}
                      </code>
                    </div>
                  </div>

                  <div className="col-span-2">
                    <span className={`font-medium text-sm ${getResponseTimeColor(r.responseTime)}`}>
                      {r.responseTime}ms
                    </span>
                  </div>

                  <div className="col-span-2">
                    <span className={`px-2 py-1 rounded-lg font-semibold text-xs ${getStatusBadgeColors(r.status)}`}>
                      {r.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table Footer + LOAD MORE */}
          <div className="bg-gray-50 border-t border-gray-200 px-5 py-5 text-center space-y-3">
            <p className="text-gray-500 font-normal text-xs">
              Showing recent requests • View all history for detailed analysis
            </p>

            {hasMore && (
              <button
                onClick={loadMore}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition"
              >
                Load More
              </button>
            )}
          </div>
        </div>

        {isFilterOpen && <div className="fixed inset-0 z-0" onClick={() => setIsFilterOpen(false)} />}
      </div>
    </div>
  );
};

export default APIUsageHistoryPage;
